import { useEffect, useReducer, useRef } from "react";
import { assertUnreachable } from "../../utilities/assertUnreachable.js";
import { useCallbackRef } from "../useCallbackRef.js";
const queryReducer = (state, action)=>{
    switch(state.status){
        case 'idle':
            return handleIdleTransitions(state, action);
        case 'loading':
            return handleLoadingTransitions(state, action);
        case 'error':
            return handleErrorTransitions(state, action);
        case 'loaded':
            return handleLoadedTransitions(state, action);
        default:
            throw new Error(assertUnreachable(state));
    }
};
const handleIdleTransitions = (state, action)=>{
    switch(action.type){
        case 'fetch':
            return {
                status: 'loading',
                mode: {
                    status: 'pre',
                    previousState: state
                },
                query: action.query
            };
        case 'complete':
        case 'failure':
        case 'reset':
        case 'full-loading':
        case 'retry':
            return state;
        default:
            throw new Error(assertUnreachable(action));
    }
};
const handleLoadingTransitions = (state, action)=>{
    switch(action.type){
        case 'fetch':
            return {
                status: 'loading',
                mode: state.mode,
                query: action.query
            };
        case 'complete':
            return {
                status: 'loaded',
                query: state.query,
                result: action.result
            };
        case 'failure':
            return {
                status: 'error',
                query: state.query,
                reason: action.reason,
                result: void 0
            };
        case 'reset':
            return {
                status: 'idle',
                query: void 0,
                result: void 0
            };
        case 'full-loading':
            return 'pre' === state.mode.status ? {
                ...state,
                mode: {
                    status: 'full',
                    previousState: state.mode.previousState
                }
            } : state;
        case 'retry':
            return state;
        default:
            throw new Error(assertUnreachable(action));
    }
};
const handleErrorTransitions = (state, action)=>{
    switch(action.type){
        case 'fetch':
            return {
                status: 'loading',
                mode: {
                    status: 'pre',
                    previousState: state
                },
                query: action.query
            };
        case 'retry':
            return {
                status: 'loading',
                mode: {
                    status: 'pre',
                    previousState: state
                },
                query: state.query
            };
        case 'reset':
            return {
                status: 'idle',
                query: void 0,
                result: void 0
            };
        case 'failure':
        case 'complete':
        case 'full-loading':
            return state;
        default:
            throw new Error(assertUnreachable(action));
    }
};
const handleLoadedTransitions = (state, action)=>{
    switch(action.type){
        case 'fetch':
            return {
                status: 'loading',
                mode: {
                    status: 'pre',
                    previousState: state
                },
                query: action.query
            };
        case 'reset':
            return {
                status: 'idle',
                query: void 0,
                result: void 0
            };
        case 'failure':
        case 'complete':
        case 'full-loading':
        case 'retry':
            return state;
        default:
            throw new Error(assertUnreachable(action));
    }
};
const internalToExternalState = (state)=>{
    switch(state.status){
        case 'loading':
            return 'pre' === state.mode.status ? state.mode.previousState : {
                status: 'loading',
                query: state.query,
                result: state.mode.previousState?.result
            };
        case 'idle':
        case 'error':
        case 'loaded':
            return state;
        default:
            throw new Error(assertUnreachable(state));
    }
};
const hasInitialQuery = (parameters)=>'initialQuery' in parameters;
const getInitialState = (parameters)=>hasInitialQuery(parameters) ? {
        status: 'loading',
        mode: {
            status: 'full'
        },
        query: parameters.initialQuery
    } : {
        status: 'idle',
        query: void 0,
        result: void 0
    };
const useQuery = (parameters)=>{
    const [internalState, dispatch] = useReducer(queryReducer, getInitialState(parameters));
    const { action } = parameters;
    const currentAction = useCallbackRef(action);
    useEffect(()=>{
        const handleSearchSuccess = (result, signal)=>{
            if (!signal.aborted) dispatch({
                type: 'complete',
                result
            });
        };
        const handleSearchFailure = (error, signal)=>{
            if (!signal.aborted) dispatch({
                type: 'failure',
                reason: error
            });
        };
        if ('loading' === internalState.status) {
            const controller = new AbortController();
            currentAction.current({
                query: internalState.query,
                signal: controller.signal
            }).then((result)=>handleSearchSuccess(result, controller.signal)).catch((error)=>handleSearchFailure(error, controller.signal));
            return ()=>controller.abort();
        }
    }, [
        internalState.query,
        internalState.status
    ]);
    const dependencies = [
        'loading' === internalState.status ? internalState.mode : void 0,
        internalState.status
    ];
    useEffect(()=>{
        if ('loading' === internalState.status && 'pre' === internalState.mode.status) {
            const timeoutId = setTimeout(()=>dispatch({
                    type: 'full-loading'
                }), 200);
            return ()=>clearTimeout(timeoutId);
        }
    }, dependencies);
    return {
        fetch: useRef((query)=>dispatch({
                type: 'fetch',
                query
            })).current,
        reset: useRef(()=>dispatch({
                type: 'reset'
            })).current,
        retry: useRef(()=>dispatch({
                type: 'retry'
            })).current,
        state: internalToExternalState(internalState)
    };
};
export { useQuery };
